// Super simple API calls - no fetching, no caching, no optimistic updates
import { updateSecondaryDevice, fetchSubDevices } from '@/lib/api/devices/devices';
import { updateZone } from '@/app/Sites/zonesApi';
import { updateSpeaker } from '@/lib/api/monitoring/speakers';
import { updateRelay } from '@/lib/api/monitoring/relays';

export const setDeviceMonitoring = async (
  nvrId: string, 
  deviceId: string, 
  monitored: boolean,
  localDevices?: { id: string; monitored?: boolean }[]
) => {
  console.log('setDeviceMonitoring called:', { nvrId, deviceId, monitored });
  
  try {
    console.log('Fetching secondary devices for metadata only:', nvrId);
    const secondaryDevices = await fetchSubDevices({ deviceId: nvrId });
    console.log('Fetched secondary devices:', secondaryDevices);
    
    if (!secondaryDevices?.length) {
      throw new Error(`No secondary devices found for NVR ${nvrId}`);
    }
    
    // Use secondary devices for complete metadata, but prefer local monitoring state if available
    const updatedDevices = secondaryDevices.map(device => {
      const localDevice = localDevices?.find(d => d.id === device.id);
      const currentMonitored = localDevice?.monitored ?? device.monitored ?? false;
      
      return {
        id: device.id!,
        monitored: device.id === deviceId ? monitored : currentMonitored,
        // Use secondary API for these metadata fields only
        description: device.description || null,
        quillNotes: device.quillNotes || null,
        capabilities: device.capabilities || {}
      };
    });
    
    console.log('Updating secondary devices:', { nvrId, updatedDevices });
    
    const payload = {
      deviceId: nvrId,
      primaryDeviceId: nvrId,
      devices: updatedDevices
    };
    
    await updateSecondaryDevice(payload);
    console.log('Successfully updated secondary devices');
    
  } catch (error) {
    console.error('setDeviceMonitoring failed:', error);
    throw error;
  }
};

export const removeDeviceFromZone = async (siteId: string, zoneId: string, deviceIds: string[]) => {
  // Simple: just remove devices from zone
  await updateZone(siteId, zoneId, {
    videoProcessorIds: deviceIds // Pass the remaining device IDs
  });
};

export const clearZone = async (siteId: string, zoneId: string) => {
  // Simple: just clear the zone
  await updateZone(siteId, zoneId, {
    videoProcessorIds: []
  });
};

export const linkSpeakerToCameras = async (speakerId: string, speakerName: string, cameraIds: string[]) => {
  // Simple: just set the speaker's camera links
  await updateSpeaker({
    id: speakerId,
    name: speakerName,
    componentGroupId: speakerId,
    relatedComponentGroups: cameraIds
  });
};

export const linkRelayToCameras = async (relayId: string, relayName: string, cameraIds: string[]) => {
  // Simple: just set the relay's camera links  
  await updateRelay({
    id: relayId,
    name: relayName,
    relatedComponentGroups: cameraIds
  });
};

// Debugging function to check secondary devices
export const logSecondaryDevices = async (nvrId: string, _label: string) => {
  const devices = await fetchSubDevices({ deviceId: nvrId });
  return devices;
};